import { createApi } from '@reduxjs/toolkit/query/react';

import { ResponseProps } from 'services/services.types';
import { myAccountApi } from 'services/myAccount/myAccount';

import { setUser } from 'store/user/userSlice';

import { logDataDogEvent } from 'utils/datadog';
import { baseQueryWithReauth } from 'utils/services';

import { DocumentItem } from 'models/document.types';

import {
  GenerateDynamicLinkQueueReqProps,
  GenerateDynamicLinkResProps,
  GetGlobalMessagesResProps,
  GetSignableDocumentsReqProps,
  GetSignableDocumentsResProps,
  GetRequiredAgreementsReqProps,
  GetRequiredAgreementsResProps,
  DeclineRequiredAgreementReqProps,
  GetTokenResProps,
  SendMedicationMigrationEnrollReqProps,
  SendMedicationMigrationEnrollResProps,
  SendMigrationDecisionReqProps,
  SignDocumentReqProps,
  SignDocumentResProps,
  SignPharmacyAgreementReqProps,
  UnSignDocumentReqProps,
  UnSignDocumentResProps,
  VerifyIdentityCrosscheckResProps,
  VerifyIdentityReqProps
} from './general.types';

// This is for any not related requests
export const generalApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    declineRequiredAgreement: build.mutation<void, DeclineRequiredAgreementReqProps>({
      invalidatesTags: ['RequiredAgreements'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(myAccountApi.util.invalidateTags(['MyAccount']));
        } catch {
          // Failed consent changes leave the current account unchanged.
        }
      },
      query: ({ type }) => ({
        method: 'POST',
        url: `/required-agreements/${type}/decline`
      })
    }),
    generateDynamicLinkQueue: build.mutation<
      GenerateDynamicLinkResProps,
      GenerateDynamicLinkQueueReqProps
    >({
      query: ({ accessToken, ...body }) => ({
        body,
        headers: {
          Authorization: accessToken
        },
        method: 'POST',
        url: '/dynamic-links'
      })
    }),
    getGlobalMessages: build.query<GetGlobalMessagesResProps, void>({
      query: () => ({
        url: '/global-messages'
      })
    }),
    getRequiredAgreements: build.query<
      GetRequiredAgreementsResProps,
      GetRequiredAgreementsReqProps
    >({
      keepUnusedDataFor: 0,
      providesTags: ['RequiredAgreements'],
      query: ({ context, preferredMedication, pricePoint }) => ({
        method: 'GET',
        params: { context, preferredMedication, pricePoint },
        url: '/required-agreements'
      })
    }),
    getSignableDocuments: build.query<GetSignableDocumentsResProps, GetSignableDocumentsReqProps>({
      keepUnusedDataFor: 0,
      query: (params) => ({
        headers: {
          ...(params?.accessToken && { Authorization: params.accessToken })
        },
        method: 'GET',
        url: '/signable-documents'
      })
    }),
    getStatesWithAsyncApptProhibition: build.query<ResponseProps<string[]>, void>({
      query: () => ({
        url: '/async-appt-prohibited-states'
      })
    }),
    getTokenForCancel: build.mutation<GetTokenResProps, { programId: string }>({
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          logDataDogEvent({
            context: { error: e },
            level: 'error',
            message: 'getTokenForCancel failed'
          });
        }
      },
      query: ({ programId }) => ({
        method: 'POST',
        url: `/typeform/${programId}/cancel-subscription`
      })
    }),
    sendMedicationMigrationEnroll: build.mutation<
      SendMedicationMigrationEnrollResProps,
      SendMedicationMigrationEnrollReqProps
    >({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(
              setUser({
                semaglutideMigration: data.data
              })
            );
          }
        } catch (e) {
          logDataDogEvent({
            context: { error: e },
            level: 'error',
            message: 'sendMedicationMigrationEnroll failed'
          });
        }
      },
      query: (body) => ({
        body,
        method: 'POST',
        url: '/medication-migration/enroll'
      })
    }),
    sendMigrationDecision: build.mutation<unknown, SendMigrationDecisionReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/medication-migration/track-action'
      })
    }),
    signDocument: build.mutation<SignDocumentResProps, SignDocumentReqProps>({
      invalidatesTags: ['RequiredAgreements'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(myAccountApi.util.invalidateTags(['MyAccount']));
        } catch {
          // Failed consent changes leave the current account unchanged.
        }
      },
      query: ({ accessToken, templateId }) => ({
        headers: {
          ...(accessToken && { Authorization: accessToken })
        },
        method: 'POST',
        url: `/sign-document/${templateId}`
      })
    }),
    signPharmacyAgreement: build.mutation<void, SignPharmacyAgreementReqProps>({
      query: ({ documentId, ...body }) => ({
        body,
        method: 'POST',
        url: `/pharmacy-agreement/${documentId}`
      })
    }),
    unSignDocument: build.mutation<UnSignDocumentResProps, UnSignDocumentReqProps>({
      query: ({ documentId, options }) => ({
        body: {
          ...options
        },
        method: 'POST',
        url: `/unsign-document/${documentId}`
      })
    }),
    verifyIdentity: build.mutation<
      ResponseProps<NonNullable<DocumentItem['identity']>>,
      VerifyIdentityReqProps
    >({
      query: ({ accessToken, ...body }) => ({
        body,
        headers: {
          ...(accessToken && { Authorization: accessToken })
        },
        method: 'POST',
        url: '/verify-identity'
      })
    }),
    verifyIdentityViaCrossCheck: build.mutation<VerifyIdentityCrosscheckResProps, void>({
      query: () => ({
        body: {
          verificationStrategy: 'crosscheck_then_dob'
        },
        method: 'POST',
        url: '/verify-identity/crosscheck'
      })
    })
  }),
  reducerPath: 'generalApi',
  tagTypes: ['General', 'RequiredAgreements']
});

export const {
  useDeclineRequiredAgreementMutation,
  useGenerateDynamicLinkQueueMutation,
  useGetGlobalMessagesQuery,
  useGetRequiredAgreementsQuery,
  useGetSignableDocumentsQuery,
  useGetStatesWithAsyncApptProhibitionQuery,
  useGetTokenForCancelMutation,
  useLazyGetGlobalMessagesQuery,
  useLazyGetStatesWithAsyncApptProhibitionQuery,
  useSendMedicationMigrationEnrollMutation,
  useSendMigrationDecisionMutation,
  useSignDocumentMutation,
  useSignPharmacyAgreementMutation,
  useUnSignDocumentMutation,
  useVerifyIdentityMutation,
  useVerifyIdentityViaCrossCheckMutation
} = generalApi;
